// Updated ChatPage.js - Integrated with main sidebar
import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import TypingEffect from './TypingEffect';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import darkLogo from '../ai-poreznik.png';
import lightLogo from '../ai-poreznik-dark.png';

// Create a component to use the logo
const TriangleIcon = () => {
  const { logoSrc } = useTheme();
  const logoToUse = logoSrc === 'ai-poreznik.png' ? darkLogo : lightLogo;
  
  return (
    <img src={logoToUse} alt="Logo" width="120" style={{ verticalAlign: 'middle' }} />
  );
};

const API_URL = process.env.REACT_APP_API_URL || 'https://worthy-optimism-production.up.railway.app/api';

const ChatPage = ({ currentConversationId, setCurrentConversationId }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [isFromHistory, setIsFromHistory] = useState(false);
  const [newMessageIndices, setNewMessageIndices] = useState(new Set());
  
  // Subscription stats
  const [queryCount, setQueryCount] = useState(0);
  const [queryLimit, setQueryLimit] = useState(null);
  const [subscriptionType, setSubscriptionType] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  
  const messagesEndRef = useRef(null);
  const navigate = useNavigate();
  const { currentUser, hasRole } = useAuth();

  // Auto-scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Fetch user stats (subscription and query count)
  useEffect(() => {
    if (!currentUser) return;
    
    const fetchUserStats = async () => {
      setStatsLoading(true);
      try {
        const response = await axios.get(`${API_URL}/subscription/stats`);
        
        if (response.data) {
          setQueryCount(response.data.queryCount || 0);
          setQueryLimit(response.data.queryLimit);
          setSubscriptionType(response.data.subscriptionType);
        }
      } catch (error) {
        console.error('Error fetching user stats:', error);
      } finally {
        setStatsLoading(false);
      }
    };
    
    fetchUserStats();
  }, [currentUser]);

  // Load selected conversation
  useEffect(() => {
    if (!currentConversationId) {
      // Reset messages when starting a new chat
      setMessages([]);
      setIsFromHistory(false);
      setNewMessageIndices(new Set());
      return;
    }
    
    const loadConversation = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API_URL}/conversations/${currentConversationId}/messages`);
        
        if (response.data && response.data.success && response.data.messages) {
          // Format messages for display
          const formattedMessages = response.data.messages.map(msg => ({
            type: msg.role,
            content: msg.content,
            sources: msg.sources || []
          }));
          
          setMessages(formattedMessages);
          // This is from history
          setIsFromHistory(true);
          // Clear new message indices since all are from history
          setNewMessageIndices(new Set());
        }
      } catch (error) {
        console.error('Error loading conversation:', error);
        setMessages([]);
        setNewMessageIndices(new Set());
      } finally {
        setLoading(false);
      }
    };
    
    loadConversation();
  }, [currentConversationId]);

  // Example queries for quick selection - tax questions for Bosnia and Herzegovina
  const exampleQueries = [
    {
      title: "Koje su stope PDV-a",
      description: "u Bosni i Hercegovini?"
    },
    {
      title: "Kako prijaviti",
      description: "porez na dohodak za freelancere?"
    },
    {
      title: "Koji su rokovi",
      description: "za predaju poreskih prijava?"
    },
    {
      title: "Koji su uslovi",
      description: "za oslobađanje od plaćanja PDV-a?"
    }
  ];

  // Handle example query click
  const handleExampleClick = (query) => {
    const fullQuery = `${query.title} ${query.description}`;
    setInput(fullQuery);
  };

  // Function to clean the response text by removing the "Reference u zakonima:" section
  const cleanResponseText = (text) => {
    if (!text) return '';
    
    // Find and remove the "Reference u zakonima:" section and everything after it
    const referenceIndex = text.indexOf("\n\nReference u zakonima:");
    if (referenceIndex !== -1) {
      return text.substring(0, referenceIndex);
    }
    
    return text;
  };
  
  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    if ((subscriptionType === 'free' || subscriptionType === null)  && queryCount >= 3 && !hasRole('admin')) {
      window.location.href = '/app/subscription-required';
      return;
    }

    const userMessage = input.trim();
    setInput('');
    
    // Remember current message count to identify new messages
    const currentMessageCount = messages.length;
    
    // Add user message to chat immediately (for UI responsiveness)
    setMessages(prevMessages => [
      ...prevMessages,
      { 
        type: 'user', 
        content: userMessage
      }
    ]);
    
    // Mark this as a new message for typing effect
    setNewMessageIndices(prev => {
      const newSet = new Set(prev);
      newSet.add(currentMessageCount);
      return newSet;
    });
    
    setLoading(true);
    
    try {
      // Create payload with query and message history
      const payload = {
        query: userMessage,
        similarityThreshold: 0.2  // Keep the default similarity threshold
      };
      
      // Include conversation ID as a STRING if it exists
      if (currentConversationId) {
        // IMPORTANT: Make sure conversationId is a STRING
        payload.conversationId = String(currentConversationId);
      }
      
      // Add message history for context - include all previous messages
      if (messages.length > 0) {
        // Format according to what the backend expects
        payload.messageHistory = messages.map(msg => ({
          role: msg.type,  // Already 'user' or 'assistant'
          content: msg.content
        }));
      }
      
      console.log('Sending API request:', payload);
      
      // Send query to backend
      const response = await axios.post(`${API_URL}/queries`, payload);
      
      console.log('API response:', response.data);
      
      if (response.data && response.data.success) {
        // Update conversation ID from response - convert to string
        if (response.data.conversationId) {
          setCurrentConversationId(String(response.data.conversationId));
          
          // If this was a new conversation, trigger refresh in the main app
          if (response.data.isNewConversation) {
            // Use a custom event to notify App component to refresh conversations
            window.dispatchEvent(new CustomEvent('refreshConversations', {
              detail: { conversationId: response.data.conversationId }
            }));
          }
        }
        
        // Update query count if provided
        if (response.data.queryCount !== undefined) {
          setQueryCount(response.data.queryCount);
        }
        
        // Add assistant response to chat
        const cleanedAnswer = response.data.answer ? cleanResponseText(response.data.answer) : 'Nema odgovora';
        
        setMessages(prevMessages => [
          ...prevMessages,
          { 
            type: 'assistant', 
            content: cleanedAnswer,
            sources: response.data.sources || []
          }
        ]);
        
        // Mark this as a new message for typing effect
        setNewMessageIndices(prev => {
          const newSet = new Set(prev);
          newSet.add(currentMessageCount + 1); // +1 for the assistant response
          return newSet;
        });
        
      } else {
        console.error('Server returned error:', response.data);
        // Handle error response
        setMessages(prevMessages => [
          ...prevMessages,
          { 
            type: 'assistant', 
            content: 'Došlo je do greške pri obradi upita. Molimo pokušajte ponovno.',
            sources: []
          }
        ]);
        
        // Mark this as a new message for typing effect
        setNewMessageIndices(prev => {
          const newSet = new Set(prev);
          newSet.add(currentMessageCount + 1);
          return newSet;
        });
      }
    } catch (error) {
      console.error('Error sending message:', error);
      
      // Check if this was a subscription limit error
      if (error.response && error.response.status === 403) {
        if (error.response.data && error.response.data.limitReached) {
          // For limited plan users who reached their limit
          setMessages(prevMessages => [
            ...prevMessages,
            { 
              type: 'assistant', 
              content: 'Dostigli ste mjesečni limit upita za vaš paket.',
              sources: []
            }
          ]);
        } else if (error.response.data && error.response.data.error === 'Subscription required') {
          // Redirect to subscription page
          navigate(`/app/subscription?count=${queryCount}`);
          return;
        } else {
          setMessages(prevMessages => [
            ...prevMessages,
            { 
              type: 'assistant', 
              content: cleanResponseText('Došlo je do greške pri komunikaciji s serverom. Molimo pokušajte ponovno.'),
              sources: []
            }
          ]);
        }
      } else {
        setMessages(prevMessages => [
          ...prevMessages,
          { 
            type: 'assistant', 
            content: 'Došlo je do greške pri komunikaciji s serverom. Molimo pokušajte ponovno.',
            sources: []
          }
        ]);
      }
      
      // Mark this as a new message for typing effect
      setNewMessageIndices(prev => {
        const newSet = new Set(prev);
        newSet.add(currentMessageCount + 1);
        return newSet;
      });
    } finally {
      setLoading(false);
      
      // Refresh user stats after sending a message
      try {
        const statsResponse = await axios.get(`${API_URL}/subscription/stats`);
        if (statsResponse.data) {
          setQueryCount(statsResponse.data.queryCount || 0);
          setQueryLimit(statsResponse.data.queryLimit);
          setSubscriptionType(statsResponse.data.subscriptionType);
        }
      } catch (err) {
        console.error('Error updating stats:', err);
      }
    }
  };

  const renderSources = (sources) => {
    if (!sources || sources.length === 0) return null;
    
    return (
      <div className="source-citation">
        {/* <p><strong>Izvor:</strong></p>
        <ul>
          {uniqueLaws.map((lawName, index) => (
            <li key={index}>
              <p>{lawName}</p>
            </li>
          ))}
        </ul> */}
      </div>
    );
  };

  // Check if a message should use typing effect
  const shouldUseTypingEffect = (index) => {
    // If not from history, or if this is a new message added in this session
    return !isFromHistory || newMessageIndices.has(index);
  };
  
  // Render the subscription status indicator
  const renderSubscriptionStatus = () => {
    if (statsLoading) return null;
    
    // Admin has unlimited access
    if (currentUser && hasRole('admin')) {
      return (
        <div className="subscription-status admin">
          Admin pristup
        </div>
      );
    }
    
    if (subscriptionType === 'start') {
      return (
        <div className="subscription-status start">
          {queryCount}/{queryLimit} upita
        </div>
      );
    }
    
    if (subscriptionType === 'pro') {
      return (
        <div className="subscription-status pro">
          {queryCount}/{queryLimit} upita
        </div>
      );
    }

    if (subscriptionType === 'premium') {
      return (
        <div className="subscription-status premium">
          {queryCount}/{queryLimit === null || queryLimit === undefined ? '∞' : queryLimit} upita
        </div>
      );
    }
    
    // Free trial user
    return (
      <div className="subscription-status free">
        Besplatno: {queryCount}/3 upita
      </div>
    );
  };

  // Custom components for ReactMarkdown
  const markdownComponents = {
    // Ensure paragraphs have proper spacing and text wrapping
    p: ({ children }) => (
      <p style={{ 
        marginBottom: '1rem',
        whiteSpace: 'pre-wrap',
        lineHeight: '1.5'
      }}>
        {children}
      </p>
    ),
    
    // Handle bullet points with better spacing
    ul: ({ children }) => (
      <ul style={{ 
        marginBottom: '1rem', 
        paddingLeft: '1.5rem',
        listStyle: 'disc'
      }}>
        {children}
      </ul>
    ),
    
    // Handle numbered lists with proper formatting
    ol: ({ children }) => (
      <ol style={{ 
        marginBottom: '1rem', 
        paddingLeft: '1.5rem',
        listStyle: 'decimal',
        counterReset: 'item'
      }}>
        {children}
      </ol>
    ),
    
    // Improve list item formatting
    li: ({ children, ordered }) => {
      // Check if this is the start of a numbered item (e.g. "1. ", "2. ")
      const isNumberedItem = typeof children === 'string' && /^\d+\.\s/.test(children);
      
      // If it's already a properly formatted list item, render normally
      if (!isNumberedItem) {
        return (
          <li style={{ 
            marginBottom: '0.75rem',
            lineHeight: '1.5'
          }}>
            {children}
          </li>
        );
      }
      
      // For text that looks like a numbered list but isn't properly formatted,
      // convert it to proper list formatting
      const content = children.replace(/^\d+\.\s/, '');
      return (
        <li style={{ 
          marginBottom: '0.75rem',
          lineHeight: '1.5',
          display: 'list-item'
        }}>
          {content}
        </li>
      );
    },
    
    // Handle line breaks
    br: () => <br />,
    
    // Handle emphasis without using **
    strong: ({ children }) => (
      <span style={{ fontWeight: 'bold' }}>{children}</span>
    ),
    em: ({ children }) => (
      <span style={{ fontStyle: 'italic' }}>{children}</span>
    )
  };

  return (
    <div className="chat-container">
      {/* Subscription status indicator */}
      {renderSubscriptionStatus()}
      
      {/* Main chat container */}
      <div className="message-container">
        <div className="messages">
          {messages.length === 0 ? (
            <>
              <div className="welcome-container">
                <div className="welcome-message">
                  <h3>AI Poreznik</h3>
                  
                  {/* Display subscription info on welcome screen */}
                  {!statsLoading && queryLimit !== null && (
                    <div className="usage-info" style={{ 
                      marginTop: '1rem', 
                      padding: '0.5rem', 
                      backgroundColor: 'var(--input-bg)', 
                      borderRadius: '8px',
                      fontSize: '0.9rem'
                    }}>
                      {currentUser && hasRole('admin') ? (
                        <p>Admin pristup</p>
                      ) : subscriptionType === 'premium' ? (
                        <p>Imate Premium plan: {queryCount} od {queryLimit === null || queryLimit === undefined ? '∞' : queryLimit} upita iskorišteno.</p>
                      ) : subscriptionType === 'pro' ? (
                        <p>Imate Pro plan: {queryCount} od {queryLimit} upita iskorišteno.</p>
                      ) : subscriptionType === 'start' ? (
                        <p>Imate Start plan: {queryCount} od {queryLimit} upita iskorišteno.</p>
                      ) : (
                        <p>Besplatna verzija: {queryCount} od 3 upita iskorišteno.</p>
                      )}
                    </div>
                  )}
                </div>
                
                <div className="examples-container">
                  {exampleQueries.map((query, index) => (
                    <div 
                      key={index} 
                      className="example-card"
                      onClick={() => handleExampleClick(query)}
                    >
                      <h4>{query.title}</h4>
                      <p>{query.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : (
            messages.map((message, index) => (
              <div 
                key={index} 
                className={`message ${message.type === 'user' ? 'user-message' : 'assistant-message'}`}
              >
                <div className="message-header">
                  {message.type === 'assistant' ? (
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
                      <TriangleIcon />
                      <span style={{ marginLeft: '0.5rem' }}></span>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
                      <span>Vaš upit</span>
                    </div>
                  )}
                </div>
                {message.type === 'assistant' ? (
                  // Only use typing effect for messages that should have it
                  shouldUseTypingEffect(index) ? (
                    <TypingEffect content={cleanResponseText(message.content)} typingSpeed={8} />
                  ) : (
                    <ReactMarkdown 
                      remarkPlugins={[remarkGfm]}
                      components={markdownComponents}
                    >
                      {cleanResponseText(message.content)}
                    </ReactMarkdown>
                  )
                ) : (
                  <ReactMarkdown 
                    remarkPlugins={[remarkGfm]}
                    components={markdownComponents}
                  >
                    {message.content}
                  </ReactMarkdown>
                )}
                {message.sources && renderSources(message.sources)}
              </div>
            ))
          )}
          {loading && (
            <div className="loading-indicator">
              <div className="dots">
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
        
        {/* Hint text added above the input form */}
        <div className="input-hint">
          Kvalitet vašeg odgovora će zavisiti u odnosu na kvalitet upita i jasnoće konteksta.
        </div>
        
        <form onSubmit={sendMessage} className="input-area">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Pošalji upit ..."
            disabled={loading}
          />
          <button 
            type="submit" 
            disabled={loading || !input.trim()}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M2.01 21L23 12L2.01 3L2 10L17 12L2 14L2.01 21Z" fill="#888888" />
            </svg>
          </button>
        </form>
      </div>
    </div>
  );
};

export default ChatPage;
